
import type { GeneratorRunCreateRequest } from "../services/generatorSizing";
import { downloadRunJson } from "../services/runExport";
import { GeneratorResultPanel } from "../components/GeneratorResultPanel";
import { GeneratorResultSummary } from "../components/GeneratorResultSummary";
import { GeneratorStudyForm } from "../components/GeneratorStudyForm";
import { GeneratorWarningPanel } from "../components/GeneratorWarningPanel";
import { RunTraceabilityPanel } from "../components/RunTraceabilityPanel";
import { SldLinkPanel } from "../components/SldLinkPanel";
import { SourceSizingSwitch } from "../components/SourceSizingSwitch";
import { StudyProjectLine } from "../components/StudyProjectLine";
import { useSldElements } from "../hooks/useSldElements";
import { useGeneratorSizing } from "../hooks/useGeneratorSizing";

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return "The DG sizing study could not be completed.";
}

// Study-page layout (Master Prompt v2.1 section 19): collapsible inputs beside
// a result-first column - summary, warnings, traceability, then detail tables.
export function GeneratorSizingPage() {
  const { calculate, reset, result, run, error, isPending, isError } = useGeneratorSizing();
  // The active revision's single-line diagram, for the source picker (A28 (d)).
  const { elements: sldElements } = useSldElements();

  // Errors are surfaced through the hook state below, not thrown into the form.
  async function handleSubmit(payload: GeneratorRunCreateRequest): Promise<void> {
    try {
      await calculate(payload);
    } catch {
      // Intentionally empty: isError / error already describe the failure.
    }
  }

  return (
    <main data-generator-sizing-page>
      <SourceSizingSwitch />
      <header>
        <p>EOS-03 · DG Sizing</p>
        <h1>DG sizing</h1>
        <p>
          Size a diesel generator set against the steady-state demand, the step load, the
          design factors and the rating schedule of the project design basis, using the
          validated KES Electrical OS calculation service. Voltage and frequency dip on load
          steps are not evaluated.
        </p>
      </header>

      <div data-study-layout>
        <section aria-labelledby="generator-study-inputs-heading">
          <details open data-study-inputs>
            <summary>
              <h2 id="generator-study-inputs-heading">Inputs</h2>
            </summary>
            <StudyProjectLine />
            <GeneratorStudyForm
              disabled={isPending}
              onSubmit={handleSubmit}
              sldElements={sldElements}
            />
          </details>
        </section>

        <section aria-labelledby="generator-study-results-heading" data-study-results>
          <h2 id="generator-study-results-heading">Results</h2>

          {isPending ? (
            <p role="status" data-calculation-state="pending">
              Calculating the generator size…
            </p>
          ) : null}

          {isError ? (
            <p role="alert" data-calculation-state="error">
              {describeError(error)}
            </p>
          ) : null}

          {result ? (
            <div data-calculation-state="success">
              <GeneratorResultSummary result={result} />
              {run ? <SldLinkPanel link={run.sld_link} /> : null}
              <GeneratorWarningPanel warnings={result.warnings} />
              {run ? (
                <RunTraceabilityPanel
                  run={run}
                  onExport={() =>
                    downloadRunJson(
                      { run, result },
                      `${run.calculation_key}-rev${run.revision_number}.json`,
                    )
                  }
                />
              ) : null}
              <GeneratorResultPanel result={result} />
              <p>
                <button type="button" onClick={reset}>
                  Clear results
                </button>
              </p>
            </div>
          ) : null}

          {!isPending && !isError && !result ? (
            <p data-calculation-state="idle">
              Submit the inputs to size a generator.
            </p>
          ) : null}
        </section>
      </div>

      <section aria-labelledby="generator-study-review-heading">
        <h2 id="generator-study-review-heading">Engineering review required</h2>
        <p>
          Calculation results are engineering evidence, not an automatic compliance
          declaration. Confirm the governing project basis, the demand data, the step load,
          the rating schedule and every growth, margin, allowance and derating factor before
          approval or issue.
        </p>
      </section>
    </main>
  );
}

export default GeneratorSizingPage;
